import { useNostr } from '@nostrify/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { STELE_CLIENT } from '@/lib/stele';

interface DeleteEventParams {
  kind: number;
  dTag: string;
  /** Optional: the event ID to target with NIP-09 kind:5 */
  eventId?: string;
}

/**
 * Hook that provides soft-delete + best-effort hard-delete for Stele events.
 *
 * 1. Publishes a tombstone (same kind + d-tag, empty content, ["deleted","true"])
 * 2. Fires a kind:5 NIP-09 deletion request (best-effort)
 */
export function useDeleteEvent() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ kind, dTag, eventId }: DeleteEventParams) => {
      if (!user) throw new Error('Not logged in');

      // Step 1: Publish tombstone
      const tombstone = await user.signer.signEvent({
        kind,
        content: '',
        tags: [
          ['d', dTag],
          ['deleted', 'true'],
          ['alt', 'Deleted Stele event'],
          ['client', STELE_CLIENT],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(tombstone, { signal: AbortSignal.timeout(5000) });

      // Step 2: Best-effort NIP-09 deletion
      if (eventId) {
        try {
          const deletion = await user.signer.signEvent({
            kind: 5,
            content: 'Deleted by Stele',
            tags: [
              ['e', eventId],
              ['a', `${kind}:${user.pubkey}:${dTag}`],
            ],
            created_at: Math.floor(Date.now() / 1000),
          });
          await nostr.event(deletion, { signal: AbortSignal.timeout(5000) });
        } catch (err) {
          console.warn('NIP-09 deletion request failed (best-effort):', err);
        }
      }

      return tombstone;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nostr', 'stele'] });
    },
  });
}
